// Notes + meeting capture.
//
// Two record kinds, one store:
//   - "kpi"     a weekly owner note attached to a KPI ("what changed since
//               last review"), shown under that KPI's card.
//   - "meeting" a free note captured during a review session, shown in the
//               Meeting Log and archived to the audit timeline.
//
// Write-through pattern mirrors the VTag discovery doc + landing-worker:
// every submission writes to localStorage immediately (so the app works
// offline / before the Worker is deployed) and, if NOTES_ENDPOINT is set,
// POSTs to the Worker. On load we merge the Worker's notes (source of truth
// for the team) with anything still local-only.

import { NOTES_ENDPOINT } from "../config/endpoints";

export type NoteKind = "kpi" | "meeting";

export interface Note {
  id: string;
  kind: NoteKind;
  /** KPI id (e.g. "s1", "sym") for kind="kpi"; null for meeting notes. */
  kpiId: string | null;
  author: string; // the signed-in email
  text: string;
  at: string; // ISO
  /** true once confirmed written to the Worker. */
  synced?: boolean;
}

const STORE_KEY = "bvndle-cc:notes";

function loadLocal(): Note[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]") as Note[];
  } catch {
    return [];
  }
}

function saveLocal(notes: Note[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(notes));
  } catch {
    /* private mode */
  }
}

function newId(): string {
  return "n_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 7);
}

/** Add a note. Always persists locally; POSTs to the Worker if configured. */
export async function addNote(input: {
  kind: NoteKind;
  kpiId: string | null;
  author: string;
  text: string;
}): Promise<Note> {
  const note: Note = {
    id: newId(),
    kind: input.kind,
    kpiId: input.kpiId,
    author: input.author,
    text: input.text.trim(),
    at: new Date().toISOString(),
    synced: false,
  };
  const local = loadLocal();
  local.push(note);
  saveLocal(local);

  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(note),
      });
      if (r.ok) {
        note.synced = true;
        const after = loadLocal().map((n) => (n.id === note.id ? note : n));
        saveLocal(after);
      }
    } catch {
      /* stays local; will surface as unsynced */
    }
  }
  return note;
}

/**
 * List all notes. Merges Worker notes (if configured) with local-only ones,
 * de-duped by id, newest first.
 */
export async function listNotes(): Promise<Note[]> {
  const local = loadLocal();
  let remote: Note[] = [];
  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/notes`);
      if (r.ok) {
        const data = (await r.json()) as { notes?: Note[] };
        remote = (data.notes ?? []).map((n) => ({ ...n, synced: true }));
      }
    } catch {
      /* fall back to local only */
    }
  }
  const byId = new Map<string, Note>();
  for (const n of local) byId.set(n.id, n);
  for (const n of remote) byId.set(n.id, n); // remote wins (it's synced)
  return Array.from(byId.values()).sort((a, b) => (a.at < b.at ? 1 : -1));
}

export const notesAreShared = Boolean(NOTES_ENDPOINT);
